import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/common/EmptyState';
import {
  useCreateWorld,
  useCreateWorldFromTemplate,
  useDeleteWorld,
  useMyWorlds,
  useWorldTemplates,
  type WorldSummaryDto,
  type WorldTemplateDto,
} from '../api/queries/worlds';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { GenerationModal } from '../components/ai/GenerationModal';

/**
 * Standalone /worlds top-level route — lists every world the creator owns, whether
 * book-bound or free-floating, with a create + create-from-template flow. Clicking
 * a world navigates to the full World Builder editor.
 */
export function WorldsRoute() {
  const [search, setSearch] = useState('');
  const worlds = useMyWorlds(search.trim() || undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [aiGenerateOpen, setAiGenerateOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorldSummaryDto | null>(
    null,
  );
  const deleteWorld = useDeleteWorld();
  const navigate = useNavigate();

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Worlds
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={() => setAiGenerateOpen(true)}
        >
          Generate with AI
        </Button>
        <Button variant="outlined" onClick={() => setTemplateOpen(true)}>
          From template
        </Button>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New world
        </Button>
      </Stack>

      <TextField
        size="small"
        label="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3, minWidth: 300 }}
      />

      {worlds.isLoading && <CircularProgress />}
      {worlds.isError && <Alert severity="error">Failed to load worlds.</Alert>}

      <Stack spacing={1}>
        {(worlds.data ?? []).map((w) => (
          <Card
            key={w.id}
            sx={{ cursor: 'pointer', position: 'relative' }}
            onClick={() =>
              navigate({ to: '/worlds/$worldId', params: { worldId: w.id } })
            }
          >
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(w);
              }}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <CardContent>
              <Typography variant="h6">{w.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {w.elementCount} element{w.elementCount === 1 ? '' : 's'}
                {w.projectId ? ' · project-scoped' : ' · standalone'}
              </Typography>
              {w.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {w.description.slice(0, 180)}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
        {worlds.data && worlds.data.length === 0 && (
          <EmptyState
            icon={<PublicIcon sx={{ fontSize: 64 }} />}
            title="Build your world"
            description="Define magic systems, locations, cultures, and more. Start from a template or create from scratch."
            ctaLabel="New World"
            onCtaClick={() => setCreateOpen(true)}
            secondaryLabel="Browse templates"
            onSecondaryClick={() => setTemplateOpen(true)}
          />
        )}
      </Stack>

      <CreateWorldDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <FromTemplateDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete world"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        onConfirm={async () => {
          if (deleteTarget) {
            await deleteWorld.mutateAsync(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* AI World Generation prompt dialog */}
      <Dialog
        open={aiGenerateOpen}
        onClose={() => setAiGenerateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generate World with AI</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Describe your world concept in 1-3 sentences. The AI will generate
              a detailed world with locations, cultures, systems, and history
              that you can use as inspiration.
            </Typography>
            <TextField
              label="World concept"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              multiline
              minRows={3}
              placeholder="A post-apocalyptic world where magic has returned after centuries of dormancy, and ancient ruins hold both power and danger"
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiGenerateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            disabled={!aiPrompt.trim()}
            onClick={() => {
              setAiGenerateOpen(false);
              setAiModalOpen(true);
            }}
          >
            Generate World
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generation modal (text mode) */}
      <GenerationModal
        open={aiModalOpen}
        onClose={() => {
          setAiModalOpen(false);
          setAiPrompt('');
        }}
        mode="text"
        seedPrompt={[
          `Create a detailed world with the following concept: ${aiPrompt}`,
          '',
          'Generate 6-8 world elements including locations, cultures, magic systems or technology, and history. For each element provide:',
          '- Name',
          '- Type (location, magic_system, culture, technology, creature, deity, etc.)',
          '- Description (2-3 paragraphs)',
          '',
          'Format as a structured list.',
        ].join('\n')}
      />
    </div>
  );
}

function CreateWorldDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateWorld(undefined);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const navigate = useNavigate();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New world</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || create.isPending}
          startIcon={
            create.isPending ? <CircularProgress size={16} /> : undefined
          }
          onClick={async () => {
            const world = await create.mutateAsync({
              name: name.trim(),
              description,
            });
            onClose();
            navigate({ to: '/worlds/$worldId', params: { worldId: world.id } });
          }}
        >
          {create.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TemplateOption({ template }: { template: WorldTemplateDto }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {template.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({template.source})
        </Typography>
        {template.elementTypes.length > 0 && (
          <Chip
            size="small"
            label={`${template.elementTypes.length} element type${template.elementTypes.length === 1 ? '' : 's'}`}
            variant="outlined"
          />
        )}
      </Stack>
      {template.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}
        >
          {template.description.length > 120
            ? template.description.slice(0, 120) + '...'
            : template.description}
        </Typography>
      )}
    </Box>
  );
}

function FromTemplateDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const templates = useWorldTemplates();
  const create = useCreateWorldFromTemplate();
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const navigate = useNavigate();

  const selectedTemplate = (templates.data ?? []).find(
    (t) => t.id === templateId,
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New world from template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Template list as selectable cards */}
          <Typography variant="body2" color="text.secondary">
            Choose a template
          </Typography>
          {templates.isLoading && <CircularProgress size={24} />}
          <Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {(templates.data ?? []).map((t) => (
              <Card
                key={t.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderColor: templateId === t.id ? 'primary.main' : 'divider',
                  borderWidth: templateId === t.id ? 2 : 1,
                  bgcolor:
                    templateId === t.id
                      ? 'action.selected'
                      : 'background.paper',
                }}
                onClick={() => setTemplateId(t.id)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <TemplateOption template={t} />
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* Selected template details */}
          {selectedTemplate && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedTemplate.name}
              </Typography>
              {selectedTemplate.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {selectedTemplate.description}
                </Typography>
              )}
              {selectedTemplate.elementTypes.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Elements included:
                  </Typography>
                  <Stack
                    direction="row"
                    sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}
                  >
                    {selectedTemplate.elementTypes.map((et) => (
                      <Chip
                        key={et}
                        size="small"
                        label={et.replace(/_/g, ' ')}
                      />
                    ))}
                  </Stack>
                </>
              )}
              {selectedTemplate.tropes.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Common tropes:
                  </Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedTemplate.tropes.map((trope) => (
                      <Chip
                        key={trope}
                        size="small"
                        label={trope.replace(/_/g, ' ')}
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </>
              )}
            </Paper>
          )}

          <TextField
            label="World name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              selectedTemplate
                ? `e.g. My ${selectedTemplate.name}`
                : 'Enter a name for your world'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!templateId || !name.trim() || create.isPending}
          startIcon={
            create.isPending ? <CircularProgress size={16} /> : undefined
          }
          onClick={async () => {
            const world = await create.mutateAsync({
              templateId,
              name: name.trim(),
            });
            onClose();
            navigate({ to: '/worlds/$worldId', params: { worldId: world.id } });
          }}
        >
          {create.isPending ? 'Creating...' : 'Apply template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
